import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { UntypedFormGroup, AbstractControl } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { FdFieldBaseComponent } from '../fd-field/fd-field.base-component';
import { sanitizeSearchFilter } from 'src/app/order/utils/sanitize-search-filter';

@Component({
    selector: 'fd-select',
    templateUrl: './fd-select.component.html',
    styleUrls: ['./fd-select.component.scss'],
    standalone: false
})
export class FdSelectComponent extends FdFieldBaseComponent implements OnInit, OnDestroy {

  private _items: Item[] = [];
  private _filter: string = '';
  private _loadError: boolean = false;
  private previousSearchValue: string = '';
  private panelKeydownHandler?: (event: KeyboardEvent) => void;

  @Input()
  field!: FdSelectConfig;

  @Input()
  parentForm!: UntypedFormGroup;

  @Output()
  loadItemsFn = new EventEmitter<string>();

  @Output()
  selectionChange = new EventEmitter();

  @ViewChild('binding', { static: false }) binding?: ElementRef;

  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild(MatSelect, { static: false }) matSelect?: MatSelect;

  @Output()
  infiniteScroll = new EventEmitter<void>();

  limit = 10;
  offset = 0;
  controlName?: string;
  context?: any;
  label?: string;
  accessibilityLabel?: string;
  placeholder?: string;
  items: Item[] = [];
  hint?: string;
  messages?: { [key: string]: string };

  focusedItem?: Item;

  onChange = (_: any) => { };
  onTouched = () => { };

  constructor(private spinner: NgxSpinnerService) {
    super();
  }

  get searchable() {
    return this.field && this.field.searchable;
  }

  async ngOnInit() {
    if(this.loadItemsFn){
      this.loadItemsFn.emit();
    }

    if (this.field && this.field.disabled) {
      this.relatedFormControl.disable();
    }
  }

  getIfSelected() {
    if (this.field && this.field.items) {
      const selectedItems = this.field.items.filter(x => x.selected);

      if (selectedItems && selectedItems.length) {
        this.relatedFormControl.setValue(selectedItems[0].value);
      }
    }
    return null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  get relatedFormControl(): AbstractControl {
    const ctrl = this.parentForm.get(this.field.controlName);
    if (!ctrl) {
      throw new Error(`Control ${this.field.controlName} not found in parentForm`);
    }
    return ctrl;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  get hasError() {
    return this.relatedFormControl.errors != null;
  }

  get errorMessages() {
    if (!this.field.messages || !this.relatedFormControl.errors) return [];
    return Object.keys(this.field.messages)
      .filter(val => this.relatedFormControl.errors && this.relatedFormControl.errors[val])
      .map(key => this.field.messages ? this.field.messages[key] : '');
  }

  onSelectionChange($event: any) {
    this.selectionChange.emit($event);
  }

  onOpenedChange(opened: boolean) {
    if (!this.searchable) {
      return;
    }

    if (!opened) {
      this.detachPanelKeydownHandler();
      return;
    }

    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      if (this.matSelect) {
        const keyManager = (this.matSelect as any)._keyManager;
        if (keyManager) {
          keyManager.setActiveItem(-1);
        }
      }
      this.attachPanelKeydownHandler();
    });
  }

  handleSpace(event: KeyboardEvent) {
    if (event.key === ' ' || event.key === 'End' || event.key === 'Home') {
      event.stopPropagation();
    }

    if (event.key === 'ArrowUp' && this.matSelect && this.searchInput) {
      const keyManager = (this.matSelect as any)._keyManager;
      if (keyManager && keyManager.activeItemIndex === 0) {
        event.preventDefault();
        keyManager.setActiveItem(-1);
        setTimeout(() => this.searchInput?.nativeElement.focus());
      }
    }
  }

  private attachPanelKeydownHandler() {
    const panel = this.matSelect?.panel?.nativeElement as HTMLElement | undefined;

    if (!panel || this.panelKeydownHandler) {
      return;
    }

    this.panelKeydownHandler = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp' || !this.matSelect || !this.searchInput) {
        return;
      }

      const keyManager = (this.matSelect as any)._keyManager;
      const activeItemIndexBeforeKeydown = keyManager?.activeItemIndex;

      if (keyManager && activeItemIndexBeforeKeydown === 0) {
        event.preventDefault();
        keyManager.setActiveItem(-1);

        setTimeout(() => {
          this.searchInput?.nativeElement.focus();
          this.searchInput?.nativeElement.scrollIntoView({ block: 'nearest' });
        });
      }
    };

    panel.addEventListener('keydown', this.panelKeydownHandler, true);
  }

  private detachPanelKeydownHandler() {
    const panel = this.matSelect?.panel?.nativeElement as HTMLElement | undefined;

    if (!panel || !this.panelKeydownHandler) {
      return;
    }

    panel.removeEventListener('keydown', this.panelKeydownHandler, true);
    this.panelKeydownHandler = undefined;
  }

  ngOnDestroy(): void {
    this.detachPanelKeydownHandler();
  }

  get selectedItem() {
    return (this.relatedFormControl && this.relatedFormControl.value) || null;
  }

  filter(event: KeyboardEvent) {
    if (this.isNavigationKey(event.key)) {
      return;
    }

    this.handleSpace(event);

    setTimeout(() => {
      const target = event.target as HTMLInputElement;
      const newSearchValue = sanitizeSearchFilter(target.value);

      if (newSearchValue !== this.previousSearchValue) {
        this.previousSearchValue = newSearchValue;
        this.loadItemsFn.emit(newSearchValue);
      }
    }, 0);
  }

  private isNavigationKey(key: string): boolean {
    const navigationKeys = [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Enter',
      'Escape',
      'Tab'
    ];
    return navigationKeys.includes(key);
  }
}

export class Item {
  label: string = '';
  value: any;
  selected?: boolean;
  markAll?: boolean;
  additionalProperties?: any;
}

export interface FdSelectConfig {
  controlName: string;
  disabled?: boolean;
  required?: boolean;
  label: string;
  searchPlaceholder?: string;
  context?: any;
  messages?: { [key: string]: string };
  searchable?: boolean;
  items: Item[];
  panelClass?: string;
}
